import { z } from 'zod';
import { currencyCodeSchema, decimalInput, isoDateSchema, optionalText, paginationQuerySchema, patchOf, } from './common.js';
import { buyerInputSchema, lineTypeSchema, partySnapshotSchema } from './parties.js';
import { calculateInvoice, isDecimal } from './calc.js';
export const DOCUMENT_TYPES = ['invoice', 'credit_note', 'debit_note'];
export const documentTypeSchema = z.enum(DOCUMENT_TYPES);
export const DOCUMENT_TYPE_LABELS = {
    invoice: 'Invoice',
    credit_note: 'Credit note',
    debit_note: 'Debit note',
};
export const coreStatusSchema = z.enum(['draft', 'issued', 'voided', 'archived']);
export const paymentStatusSchema = z.enum(['unpaid', 'partially_paid', 'paid']);
const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'Use HH:MM');
// ───────────────────────── input ─────────────────────────
export const lineTaxInputSchema = z.object({
    taxCategoryId: z.uuid(),
    taxPercent: decimalInput({ min: 0, max: 100 }),
});
export const invoiceLineInputSchema = z.object({
    productServiceId: z.uuid().nullish(),
    lineType: lineTypeSchema.default('goods'),
    clientItemCode: optionalText(60),
    hsnCode: optionalText(40),
    productCategory: optionalText(100),
    isicCode: optionalText(20),
    serviceCategory: optionalText(100),
    itemName: z.string().trim().min(1, 'Enter an item name').max(300),
    itemDescription: optionalText(2000),
    sellersItemIdentification: optionalText(60),
    invoicedQuantity: decimalInput({ min: 0.000001 }),
    priceAmount: decimalInput({ min: 0 }),
    baseQuantity: decimalInput({ min: 0.000001 }).default('1'),
    priceUnit: z.string().trim().min(1).max(20).default('EA'),
    discountRate: decimalInput({ min: 0, max: 100 }).nullish(),
    discountAmount: decimalInput({ min: 0 }).nullish(),
    feeRate: decimalInput({ min: 0, max: 100 }).nullish(),
    feeAmount: decimalInput({ min: 0 }).nullish(),
    taxes: z.array(lineTaxInputSchema).max(5).default([]),
});
const draftHeaderShape = {
    documentType: documentTypeSchema.default('invoice'),
    issueDate: isoDateSchema,
    issueTime: timeSchema.nullish(),
    dueDate: isoDateSchema.nullish(),
    documentCurrencyCode: currencyCodeSchema,
    taxCurrencyCode: currencyCodeSchema.nullish(),
    exchangeRate: decimalInput({ min: 0.00000001 }).nullish(),
    branchId: z.uuid().nullish(),
    billingReferenceId: z.uuid().nullish(),
    customerId: z.uuid().nullish(),
    /** One-off buyer; ignored when customerId is supplied. */
    buyer: buyerInputSchema.nullish(),
    note: optionalText(2000),
    sourceReference: optionalText(200),
    lines: z.array(invoiceLineInputSchema).max(500).default([]),
};
export const invoiceDraftInputSchema = z.object({
    taxpayerProfileId: z.uuid(),
    ...draftHeaderShape,
});
/** PATCH: any header field; `lines`, when present, replaces the entire line list. */
export const invoiceDraftUpdateSchema = patchOf(z.object({ ...draftHeaderShape }))
    .extend({
    lines: z.array(invoiceLineInputSchema).max(500).optional(),
    /** Optimistic concurrency: reject the save if the draft changed since this timestamp. */
    expectedUpdatedAt: z.string().optional(),
});
export const invoiceListQuerySchema = paginationQuerySchema.extend({
    taxpayerProfileId: z.uuid().optional(),
    status: coreStatusSchema.optional(),
    paymentStatus: paymentStatusSchema.optional(),
    documentType: documentTypeSchema.optional(),
    customerId: z.uuid().optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    overdue: z.stringbool().optional(),
    nrsStatus: z.enum(['none', 'queued', 'submitted', 'processing', 'completed', 'failed', 'outcome_unknown']).optional(),
});
export const recordPaymentSchema = z.object({
    amount: decimalInput({ min: 0.01 }),
    paymentDate: isoDateSchema,
    paymentMethod: optionalText(40),
    reference: optionalText(120),
    notes: optionalText(1000),
});
export const voidInvoiceSchema = z.object({ reason: z.string().trim().min(3, 'Give a reason').max(500) });
export const createCorrectionSchema = z.object({
    documentType: z.enum(['credit_note', 'debit_note']),
    /** Original line numbers to copy; omit to copy every line. */
    lineNumbers: z.array(z.number().int().min(1)).optional(),
    reason: optionalText(500),
});
/**
 * Issue-time validation shared by the API (authoritative) and the editor
 * (inline guidance). Paths use dotted notation matching the form field names so
 * the UI can focus the offending control.
 */
export function validateInvoice(inv, ctx) {
    const out = [];
    const err = (path, code, message) => out.push({ path, code, message, severity: 'error' });
    const warn = (path, code, message) => out.push({ path, code, message, severity: 'warning' });
    if (!inv.issueDate)
        err('issueDate', 'required', 'Choose an issue date.');
    if (inv.issueDate && inv.dueDate && inv.dueDate < inv.issueDate) {
        err('dueDate', 'due_before_issue', 'The due date cannot be before the issue date.');
    }
    if (!inv.documentCurrencyCode)
        err('documentCurrencyCode', 'required', 'Choose a currency.');
    if (inv.taxCurrencyCode &&
        inv.documentCurrencyCode &&
        inv.taxCurrencyCode !== inv.documentCurrencyCode &&
        !(inv.exchangeRate && isDecimal(inv.exchangeRate) && Number(inv.exchangeRate) > 0)) {
        err('exchangeRate', 'exchange_rate_required', 'Enter the exchange rate to the tax currency.');
    }
    if ((inv.documentType === 'credit_note' || inv.documentType === 'debit_note') &&
        !inv.billingReferenceId) {
        err('billingReferenceId', 'billing_reference_required', 'Link the original invoice this note corrects.');
    }
    if (!inv.buyer?.legalName?.trim()) {
        err('buyer.legalName', 'buyer_required', 'Choose a customer or enter the buyer’s name.');
    }
    if (!ctx.supplier.legalName?.trim()) {
        err('supplier.legalName', 'supplier_required', 'The business profile is missing a legal name.');
    }
    if (inv.lines.length === 0) {
        err('lines', 'lines_required', 'Add at least one line item.');
    }
    inv.lines.forEach((l, i) => {
        if (!l.itemName?.trim())
            err(`lines.${i}.itemName`, 'required', 'Enter an item name.');
        const taxes = l.taxes ?? [];
        taxes.forEach((t, j) => {
            if (ctx.validTaxCategoryIds && !ctx.validTaxCategoryIds.has(t.taxCategoryId)) {
                err(`lines.${i}.taxes.${j}.taxCategoryId`, 'unknown_tax_category', 'Choose a valid tax category.');
            }
        });
        if (taxes.length === 0) {
            warn(`lines.${i}.taxes`, 'no_tax', 'No tax is applied to this line.');
        }
    });
    const calc = calculateInvoice(inv.lines);
    for (const c of calc.issues)
        err(c.path, c.code, c.message);
    if (inv.lines.length > 0 && Number(calc.payableAmount) === 0) {
        warn('lines', 'zero_total', 'The invoice total is zero.');
    }
    // Workflow-specific requirements. These reflect the Portal's current
    // assumptions and will be revisited once the SI contract is confirmed.
    if (ctx.workflowMode !== 'standalone') {
        const sev = ctx.workflowMode === 'nrs_required' ? err : warn;
        const suffix = ctx.workflowMode === 'nrs_required'
            ? ' It is required before this invoice can be sent for NRS clearance.'
            : ' It will be needed if you send this invoice for NRS clearance.';
        if (!ctx.supplier.taxIdentifier?.trim()) {
            sev('supplier.taxIdentifier', 'supplier_tax_id', `Add your tax identifier in Business settings.${suffix}`);
        }
        if (!ctx.supplier.streetName?.trim() || !ctx.supplier.cityName?.trim()) {
            sev('supplier.address', 'supplier_address', `Add your street and city in Business settings.${suffix}`);
        }
        if (!inv.buyer?.taxIdentifier?.trim()) {
            sev('buyer.taxIdentifier', 'buyer_tax_id', `Add the buyer’s tax identifier.${suffix}`);
        }
        if (!inv.buyer?.streetName?.trim() || !inv.buyer?.cityName?.trim()) {
            sev('buyer.address', 'buyer_address', `Add the buyer’s street and city.${suffix}`);
        }
    }
    return out;
}
// ───────────────────────── responses ─────────────────────────
const money = z.string();
export const invoiceLineSchema = z.object({
    id: z.uuid(),
    lineNo: z.number(),
    productServiceId: z.uuid().nullable(),
    lineType: lineTypeSchema,
    clientItemCode: z.string().nullable(),
    hsnCode: z.string().nullable(),
    productCategory: z.string().nullable(),
    isicCode: z.string().nullable(),
    serviceCategory: z.string().nullable(),
    itemName: z.string(),
    itemDescription: z.string().nullable(),
    sellersItemIdentification: z.string().nullable(),
    invoicedQuantity: z.string(),
    priceAmount: z.string(),
    baseQuantity: z.string(),
    priceUnit: z.string(),
    discountRate: z.string().nullable(),
    discountAmount: z.string().nullable(),
    feeRate: z.string().nullable(),
    feeAmount: z.string().nullable(),
    lineExtensionAmount: money,
    taxes: z.array(z.object({
        taxCategoryId: z.uuid(),
        taxCategoryCode: z.string(),
        taxCategoryName: z.string(),
        taxPercent: z.string(),
        taxableAmount: money,
        taxAmount: money,
    })),
});
export const taxSubtotalSchema = z.object({
    taxCategoryId: z.uuid(),
    taxCategoryCode: z.string(),
    taxCategoryName: z.string(),
    taxPercent: z.string(),
    taxableAmount: money,
    taxAmount: money,
});
export const INVOICE_INTEGRATION_STATUSES = [
    'queued',
    'submitted',
    'processing',
    'completed',
    'failed',
    'outcome_unknown',
];
export const invoiceIntegrationSchema = z.object({
    id: z.uuid(),
    businessIntegrationId: z.uuid(),
    status: z.enum(INVOICE_INTEGRATION_STATUSES),
    /** Present only after the SI returned it. Never generated locally. */
    irn: z.string().nullable(),
    qrPayload: z.string().nullable(),
    clearanceStatus: z.string().nullable(),
    exchangeStatus: z.string().nullable(),
    submittedAt: z.string().nullable(),
    lastSyncedAt: z.string().nullable(),
    lastErrorCode: z.string().nullable(),
    lastErrorMessage: z.string().nullable(),
    /** True when the result came from the development mock adapter. */
    simulated: z.boolean(),
    providerCode: z.string(),
});
export const invoiceSummarySchema = z.object({
    id: z.uuid(),
    taxpayerProfileId: z.uuid(),
    invoiceNumber: z.string(),
    numbered: z.boolean(),
    documentType: documentTypeSchema,
    coreStatus: coreStatusSchema,
    paymentStatus: paymentStatusSchema,
    overdue: z.boolean(),
    issueDate: z.string(),
    dueDate: z.string().nullable(),
    documentCurrencyCode: z.string(),
    buyerName: z.string().nullable(),
    customerId: z.uuid().nullable(),
    payableAmount: money,
    paidAmount: money,
    outstandingAmount: money,
    sourceChannel: z.enum(['portal', 'import', 'app_inbound']),
    nrsStatus: z.enum(INVOICE_INTEGRATION_STATUSES).nullable(),
    nrsCleared: z.boolean(),
    updatedAt: z.string(),
});
export const invoiceSchema = invoiceSummarySchema.extend({
    direction: z.enum(['outgoing', 'incoming']),
    issueTime: z.string().nullable(),
    taxCurrencyCode: z.string().nullable(),
    exchangeRate: z.string().nullable(),
    branchId: z.uuid().nullable(),
    billingReferenceId: z.uuid().nullable(),
    billingReferenceNumber: z.string().nullable(),
    sourceReference: z.string().nullable(),
    supplier: partySnapshotSchema,
    buyer: partySnapshotSchema,
    note: z.string().nullable(),
    voidReason: z.string().nullable(),
    lineExtensionAmount: money,
    taxExclusiveAmount: money,
    taxTotalAmount: money,
    taxInclusiveAmount: money,
    creditedAmount: money,
    lines: z.array(invoiceLineSchema),
    taxSubtotals: z.array(taxSubtotalSchema),
    integration: invoiceIntegrationSchema.nullable(),
    issuedAt: z.string().nullable(),
    createdAt: z.string(),
    editable: z.boolean(),
    corrections: z.array(z.object({ id: z.uuid(), invoiceNumber: z.string(), documentType: documentTypeSchema, coreStatus: coreStatusSchema, payableAmount: money })),
});
export const paymentSchema = z.object({
    id: z.uuid(),
    invoiceId: z.uuid(),
    amount: money,
    currencyCode: z.string(),
    paymentDate: z.string(),
    paymentMethod: z.string().nullable(),
    reference: z.string().nullable(),
    notes: z.string().nullable(),
    recordedByName: z.string().nullable(),
    createdAt: z.string(),
    nrsReport: z
        .object({ status: z.enum(['pending', 'submitted', 'confirmed', 'failed', 'outcome_unknown']), lastErrorMessage: z.string().nullable(), confirmedAt: z.string().nullable() })
        .nullable(),
});
export const activitySchema = z.object({
    id: z.uuid(),
    eventType: z.string(),
    actorType: z.enum(['user', 'system', 'integration']),
    actorName: z.string().nullable(),
    message: z.string().nullable(),
    details: z.unknown().nullable(),
    createdAt: z.string(),
});
export const validationResultSchema = z.object({
    valid: z.boolean(),
    issues: z.array(z.object({
        path: z.string(),
        code: z.string(),
        message: z.string(),
        severity: z.enum(['error', 'warning']),
    })),
    totals: z.object({
        lineExtensionAmount: money,
        taxExclusiveAmount: money,
        taxTotalAmount: money,
        taxInclusiveAmount: money,
        payableAmount: money,
    }),
    nextInvoiceNumber: z.string().nullable(),
});
export const documentSchema = z.object({
    id: z.uuid(),
    invoiceId: z.uuid().nullable(),
    kind: z.enum(['uploaded_source', 'generated_pdf', 'export', 'attachment']),
    fileName: z.string(),
    mimeType: z.string(),
    fileSize: z.number(),
    createdAt: z.string(),
});
//# sourceMappingURL=invoices.js.map